import { useCallback } from 'react';
import { useGeoMarkupStore } from '../store';
import { Coordinates } from '../types';
import { useTabAwareArtifactNavigation } from './useTabAwareArtifactNavigation';

/**
 * Custom hook that provides task navigation functionality with unsaved changes protection.
 *
 * Since April 2026, navigation pushes the overlay URL (`/map/task/:id`)
 * instead of setting store flags directly. `useSyncOverlayWithUrl` reflects
 * the new URL into store state.
 */
export const useTaskNavigation = () => {
  const isTaskFormOpen = useGeoMarkupStore((s) => s.tasks.formIsOpen);
  const selectedTaskId = useGeoMarkupStore((s) => s.tasks.selectedId);
  const selectedTaskShortId = useGeoMarkupStore(
    (s) => s.tasks.selectedTask?.shortId ?? null
  );
  const showNavigationDialogState = useGeoMarkupStore(
    (s) => s.navigation.showDialog
  );
  const pending = useGeoMarkupStore((s) => s.navigation.pending);
  const showNavigationDialog = useGeoMarkupStore((s) => s.navigation.show);
  const hideNavigationDialog = useGeoMarkupStore((s) => s.navigation.hide);

  const { navigateToTaskFromList } = useTabAwareArtifactNavigation();

  const executePendingNavigation = useCallback(() => {
    if (!pending) return;
    const { taskId, coordinates } = pending;
    navigateToTaskFromList(taskId, coordinates);
    hideNavigationDialog();
  }, [pending, navigateToTaskFromList, hideNavigationDialog]);

  /**
   * Navigate to task details with unsaved changes protection.
   * @param taskId - ID of the task to navigate to
   * @param coordinates - Coordinates of the task for zooming
   * @returns boolean - true if navigation was successful, false if cancelled or pending
   */
  const navigateToTaskDetails = useCallback(
    (taskId: string, coordinates: Coordinates): boolean => {
      // Block navigation completely when task form is open.
      // This prevents accidentally closing the form (which would clear
      // geometries via mutual exclusion).
      if (isTaskFormOpen) {
        // Resolve "preview" to actual task ID (preview geometries belong
        // to currently edited task).
        const resolvedTaskId = taskId === 'preview' ? selectedTaskId : taskId;

        // Show dialog when clicking different task (regardless of active tool).
        // Check both GUID and shortId so the comparison works when the
        // incoming id could be either format.
        const isSameTask =
          resolvedTaskId === selectedTaskId ||
          resolvedTaskId === selectedTaskShortId;
        if (resolvedTaskId && !isSameTask) {
          showNavigationDialog(taskId, coordinates);
        }
        return false; // Always block navigation when form is open
      }

      navigateToTaskFromList(taskId, coordinates);

      return true;
    },
    [
      isTaskFormOpen,
      selectedTaskId,
      selectedTaskShortId,
      navigateToTaskFromList,
      showNavigationDialog,
    ]
  );

  return {
    navigateToTaskDetails,
    isTaskFormOpen,
    showNavigationDialog: showNavigationDialogState,
    executePendingNavigation,
    cancelNavigation: hideNavigationDialog,
  };
};
